/*
 * Обращения клиента на фронтенде: окно входящего звонка (WebPhone) и лента заявки (RequestHistoryActions).
 *
 * Чистые функции без Vue и axios. CommonJS (module.exports) — default-import в компоненты,
 * require в Node-тесте (tests/js/appeals.test.js). Контракт API — docs/appeals.md.
 *
 * Здесь три вещи:
 *  1. TEXTS, REQUEST_PEOPLE — все подписи окна и ленты и выбор «кого показывать у найденной заявки»
 *     в одном месте: менять их нужно только здесь;
 *  2. окно входящего: «этот ответ — тот звонок, что звонит сейчас?» (isOwnCall) и «ответ API → вариант окна» (incomingView);
 *     колонка исходящего в той же форме (outgoingView);
 *  3. лента заявки: «обращения заявки → записи ленты» (appealBlocks): каждое повторное обращение, звонок или лид, — своя запись,
 *     строка самого звонка в ленте не меняется.
 *
 * Источники сравнивает сервер (other_source, по id) — здесь только читаем готовый признак.
 */
var outgoingNumbers = require('../components/webphone/outgoingNumbers');

// ---------------------------------------------------------------------------------------------------------------------
// Тексты и выбор «кого показывать»
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Склонение по числу: plural(1, 'день', 'дня', 'дней') → «день»; 2 → «дня»; 5 → «дней»; 11 → «дней»; 21 → «день».
 */
function plural(n, one, few, many) {
    var mod10 = n % 10;
    var mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

var TEXTS = {
    // --- окно входящего звонка ---
    // Подписи вариантов — как в ТЗ (HTML-прототип): под заявкой показывается строка, поясняющая вариант. Автор подтвердил, что они нужны.
    // «Будет создана» — формулировка ТЗ; к моменту показа окна заявка уже создана (решение принимает сервер при создании звонка),
    // если нужно «Создана новая заявка» — поменять здесь.
    sourceOfCall: 'Источник текущего звонка',
    dialedNumber: 'На номер:',
    sourceUnknown: 'Источник не определён',
    dateUnknown: 'Дата не указана',
    noRequests: 'Заявок не найдено',
    newRequest: 'Создана новая заявка',
    newRequestWithCallSource: 'Создана новая заявка с источником звонка',
    otherSource: 'Другой источник',
    sameSource: 'Тот же источник · повторное обращение',
    /** «Срок дубля 14 дней истёк»; без срока (салон не отдан) — «Срок дубля истёк» */
    duplicateExpired: function (days) {
        return days > 0 ? 'Срок дубля ' + days + ' ' + plural(days, 'день', 'дня', 'дней') + ' истёк' : 'Срок дубля истёк';
    },
    requestTitle: 'Заявка №',
    previousRequestTitle: 'Предыдущая заявка №',
    requestSource: 'Источник заявки:',
    previousRequestSource: 'Источник:',
    // Исходящий: та же форма, что у входящего (ТЗ): источник заявки и номер, с которого звоним
    outgoingSource: 'Источник заявки',
    outgoingFrom: 'С номера:',

    // --- лента заявки: запись на каждое повторное обращение (звонок или лид) ---
    feedRepeat: 'Повторное обращение',      // appeal.kind === 'repeat'; первое обращение в ленте не показывается
    feedOtherSource: 'ДРУГОЙ ИСТОЧНИК',      // оранжевая метка (appeal.other_source) — единственное, что выделено цветом
    feedSameSource: 'тот же источник',       // не метка, а мелкий серый текст у повторного обращения с тем же источником
    feedDialed: 'на номер',                  // у звонка: «на номер +7 909 021-07-53 · Avito»
    feedLeadSource: 'Источник:',             // у лида: «Источник: Дром.ру АвтоМаркет»
    feedDouble: 'дубль №',                   // «дубль №2» — ссылка на заявку-дубль, которую создал повторный лид
};

/**
 * Кого показывать у найденной заявки в окне входящего (строка «Оператор: …»). Автор решил: только оператор, менеджер не нужен;
 * вернуть его — добавить { key: 'sales_manager', label: 'Менеджер', skipIfWindowRequest: false }.
 * skipIfWindowRequest: у duplicate найденная заявка — та же, что слева в окне (имя, площадка, оператор из
 * requests/findByPhone), поэтому оператора второй раз не пишем; у expired слева новая заявка, а тут — предыдущая.
 */
var REQUEST_PEOPLE = [
    { key: 'operator', label: 'Оператор', skipIfWindowRequest: true },
];

// ---------------------------------------------------------------------------------------------------------------------
// Время
// ---------------------------------------------------------------------------------------------------------------------

// Часовой пояс CRM — config/app.php, 'Europe/Moscow' (UTC+3, летнего времени нет). API отдаёт время строкой
// «Y-m-d H:i:s» без пояса, и сравнивать её с «сейчас» надо в поясе CRM, а не браузера: оператор может сидеть
// в другом часовом поясе. Если пояс сервера поменяется — поменять здесь (или отдавать с сервера ISO-время с поясом).
var CRM_UTC_OFFSET_MINUTES = 180;

var MS_PER_MINUTE = 60 * 1000;
// «Старше пары минут — не наш звонок» (docs/appeals.md)
var MAX_CALL_AGE_MS = 2 * MS_PER_MINUTE;
// Исключение для очереди (incoming_call_mode = multiple): запись звонка создаётся в начале ожидания, а оператору
// звонят, когда он освободится, — через минуты. Пока звонок идёт (статус NEW), он наш: демон ждёт не дольше
// часа ($max_wait в MultipleIncoming), дольше запись жить как «звонящая» не может.
var MAX_QUEUE_WAIT_MS = 60 * MS_PER_MINUTE;
// Запись «из будущего» — признак не того пояса или сбитых часов: ей не доверяем (иначе показали бы чужой звонок)
var MAX_FUTURE_MS = 10 * MS_PER_MINUTE;
var STATUS_NEW = 'NEW';

// Запись звонка и INVITE оператору появляются почти одновременно, поэтому первый ответ может прийти до записи:
// пустой или с прошлым звонком клиента. Паузы между повторами (мс); исчерпали — окно как раньше.
var RETRY_DELAYS_MS = [400, 800, 1200, 1800];

/**
 * «Y-m-d H:i:s» (время CRM) → миллисекунды Unix. Строка с поясом (ISO) читается как есть. Не время → NaN.
 */
function parseCrmTime(value) {
    if (value === null || value === undefined) return NaN;
    var raw = String(value).trim();
    if (/(Z|[+-]\d{2}:?\d{2})$/i.test(raw) && /T/.test(raw)) return Date.parse(raw);
    var m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(raw);
    if (!m) return NaN;
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) - CRM_UTC_OFFSET_MINUTES * MS_PER_MINUTE;
}

/**
 * «Сейчас» для сверки со звонком: время сервера из заголовка Date самого ответа (часы компьютера оператора
 * могут уходить на минуты), а если заголовка нет — время INVITE по часам браузера.
 *
 * @param {{headers?: Object}|null} response ответ axios
 * @param {number} inviteAtMs Date.now() в момент INVITE
 */
function referenceTime(response, inviteAtMs) {
    var header = response && response.headers ? response.headers.date : null;
    var parsed = header ? Date.parse(header) : NaN;
    return isNaN(parsed) ? inviteAtMs : parsed;
}

/**
 * Ответ latestIncomingByPhone — это звонок, который звонит сейчас, а не прошлый звонок этого клиента?
 *  - записи нет, время не читается или она «из будущего» — нет;
 *  - не старше пары минут от «сейчас» — да;
 *  - старше: да только пока звонок идёт (NEW) и ждал не дольше часа (очередь, см. MAX_QUEUE_WAIT_MS).
 *
 * @param {Object|null} call ответ API
 * @param {number} referenceMs см. referenceTime()
 * @returns {boolean}
 */
function isOwnCall(call, referenceMs) {
    if (!call || !call.called_at) return false;
    var calledAt = parseCrmTime(call.called_at);
    if (isNaN(calledAt) || isNaN(referenceMs)) return false;
    var age = referenceMs - calledAt;
    if (age < -MAX_FUTURE_MS) return false;
    if (age <= MAX_CALL_AGE_MS) return true;
    return call.status === STATUS_NEW && age <= MAX_QUEUE_WAIT_MS;
}

/** «2026-10-08 16:16:00» → «08.10.2026, 16:16» (строкой, без часовых поясов). Не время → ''. */
function formatDate(value) {
    var m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(value === null || value === undefined ? '' : value));
    return m ? m[3] + '.' + m[2] + '.' + m[1] + ', ' + m[4] + ':' + m[5] : '';
}

// ---------------------------------------------------------------------------------------------------------------------
// Окно входящего звонка
// ---------------------------------------------------------------------------------------------------------------------

var VARIANT = {
    OTHER_SOURCE: 'other_source',   // есть заявка, источник звонка другой
    SAME_SOURCE: 'same_source',     // есть заявка, источник тот же
    NEW: 'new',                     // заявок не было
    EXPIRED: 'expired',             // заявка есть, но старше «Возраста дубля»
};

/**
 * Вариант окна по таблице из docs/appeals.md. mode null (старый звонок, нет доступа к заявке, сбой записи)
 * и неизвестный режим — null: окно показывает только то, что знало раньше.
 */
function variantOf(call) {
    if (!call) return null;
    switch (call.mode) {
        case 'duplicate': return call.other_source ? VARIANT.OTHER_SOURCE : VARIANT.SAME_SOURCE;
        case 'new': return VARIANT.NEW;
        case 'expired': return VARIANT.EXPIRED;
        default: return null;
    }
}

/**
 * Источник текущего звонка: у обращения; пока записи звонка нет — по номеру набора; нет доступа к салону
 * (source = null) — название номера, как в прежней плашке окна.
 */
function sourceNameOf(sourcePhone, call) {
    if (call && call.source && call.source.name) return call.source.name;
    if (sourcePhone && sourcePhone.source && sourcePhone.source.name) return sourcePhone.source.name;
    if (sourcePhone && sourcePhone.title) return sourcePhone.title;
    return null;
}

function dialedNumberOf(sourcePhone, call) {
    var number = (sourcePhone && sourcePhone.number) || (call && call.dst) || null;
    return number ? outgoingNumbers.formatPhone(number) : null;
}

function peopleOf(request, windowRequest) {
    return REQUEST_PEOPLE
        .filter(function (person) {
            return !(windowRequest && person.skipIfWindowRequest) && request[person.key] && request[person.key].name;
        })
        .map(function (person) {
            return { label: person.label, name: request[person.key].name };
        });
}

/**
 * Найденная заявка (previous_request): у duplicate — заявка звонка, у expired — просроченная.
 * Нет доступа к ней (previous_request = null) — блока нет, остаётся источник звонка.
 */
function requestBlockOf(call, variant) {
    var request = call.previous_request;
    if (!request) return null;
    var expired = variant === VARIANT.EXPIRED;
    return {
        id: request.id,
        title: (expired ? TEXTS.previousRequestTitle : TEXTS.requestTitle) + request.id,
        date: formatDate(request.created_at) || TEXTS.dateUnknown,
        sourceLabel: expired ? TEXTS.previousRequestSource : TEXTS.requestSource,
        sourceName: request.source ? request.source.name : null,
        people: peopleOf(request, !expired),
    };
}

/**
 * Строки-подписи под заявкой по порядку; tone: plain | ok (зелёная) | warning (жёлтая, с треугольником) | muted (серая).
 * Возраст дубля для «Срок дубля N дней истёк» — platform.age_double звонка (в сутках).
 */
function noticesOf(call, variant) {
    switch (variant) {
        case VARIANT.NEW:
            return [{ tone: 'plain', text: TEXTS.noRequests }, { tone: 'ok', text: TEXTS.newRequest }];
        case VARIANT.EXPIRED:
            return [
                { tone: 'warning', text: TEXTS.duplicateExpired(call.platform ? +call.platform.age_double : 0) },
                { tone: 'plain', text: TEXTS.newRequestWithCallSource },
            ];
        case VARIANT.OTHER_SOURCE:
            return [{ tone: 'warning', text: TEXTS.otherSource }];
        case VARIANT.SAME_SOURCE:
            return [{ tone: 'muted', text: TEXTS.sameSource }];
        default:
            return [];
    }
}

/**
 * Что показать в колонке обращения окна входящего.
 *
 * @param {Object|null} sourcePhone ответ source_phones/findByPhone — известен сразу по Diversion
 * @param {Object|null} call свежая запись звонка из latestIncomingByPhone (isOwnCall); null — её нет или она не наша
 * @returns {{
 *   visible: boolean, variant: string|null, accent: boolean, sourceLabel: string, sourceName: string|null,
 *   numberLabel: string, dialedNumber: string|null,
 *   request: null|{id:number,title:string,date:string,sourceLabel:string,sourceName:string|null,people:Array<{label:string,name:string}>},
 *   notices: Array<{tone:string,text:string}>
 * }}
 *  visible — есть что показать (иначе колонки нет и окно двухколоночное, как раньше);
 *  variant — null: решения нет, показывается только источник по номеру набора;
 *  request — найденная заявка; когда заявок нет и без решения её нет;
 *  notices — подписи варианта под заявкой (см. noticesOf); без решения их нет;
 *  accent — жёлтая планка у источника (только «другой источник»).
 */
function incomingView(sourcePhone, call) {
    var variant = variantOf(call);
    var name = sourceNameOf(sourcePhone, call);
    return {
        visible: !!(name || variant),
        variant: variant,
        accent: variant === VARIANT.OTHER_SOURCE,
        sourceLabel: TEXTS.sourceOfCall,
        sourceName: name || (variant ? TEXTS.sourceUnknown : null),
        numberLabel: TEXTS.dialedNumber,
        dialedNumber: dialedNumberOf(sourcePhone, call),
        request: variant ? requestBlockOf(call, variant) : null,
        notices: variant ? noticesOf(call, variant) : [],
    };
}

/**
 * Колонка панели исходящего звонка — та же форма, что у входящего (ТЗ): «Источник заявки» (основной источник заявки,
 * по которой звоним), «С номера: …» (номер, выбранный в окне выбора номера) и ниже ссылка на заявку с датой создания.
 * Источник приходит отдельным запросом (requests/findByPhone его не отдаёт): пока он не загружен (source = null), колонки нет,
 * чтобы не показывать «Источник не определён» на время загрузки.
 *
 * @param {Object|null} request заявка, по которой звоним (requests/findByPhone)
 * @param {{name: string|null}|null} source источник заявки; null — ещё грузится, name пуст — не определён
 * @param {string|null} fromNumber исходящий номер из окна выбора номера
 * @returns тот же вид, что у incomingView (variant всегда null, планки нет)
 */
function outgoingView(request, source, fromNumber) {
    var empty = {
        visible: false, variant: null, accent: false, sourceLabel: TEXTS.outgoingSource, sourceName: null,
        numberLabel: TEXTS.outgoingFrom, dialedNumber: null, request: null, notices: [],
    };
    if (!request || !source) return empty;
    return Object.assign(empty, {
        visible: true,
        sourceName: source.name || TEXTS.sourceUnknown,
        dialedNumber: fromNumber ? outgoingNumbers.formatPhone(fromNumber) : null,
        request: {
            id: request.id,
            title: TEXTS.requestTitle + request.id,
            date: formatDate(request.created_at) || TEXTS.dateUnknown,
            sourceLabel: null,
            sourceName: null,
            people: [],
        },
    });
}

// ---------------------------------------------------------------------------------------------------------------------
// Лента заявки
// ---------------------------------------------------------------------------------------------------------------------

/** Вторая строка записи: у звонка «на номер +7 … · источник», у лида «Источник: …»; null — показывать нечего */
function appealLine(appeal) {
    var sourceName = appeal.source && appeal.source.name ? appeal.source.name : null;
    if (appeal.channel === 'lead') return sourceName ? TEXTS.feedLeadSource + ' ' + sourceName : null;
    var parts = [];
    if (appeal.dialed_number) parts.push(TEXTS.feedDialed + ' ' + outgoingNumbers.formatPhone(appeal.dialed_number));
    if (sourceName) parts.push(sourceName);
    return parts.length ? parts.join(' · ') : null;
}

/**
 * Записи ленты по ответу GET /api/requests/{id}/appeals: повторные обращения заявки, каждое своей записью, — и звонки, и лиды.
 * Первое обращение (appeal.kind = first) не показываем: это звонок или лид, создавший заявку, он и так в ленте
 * (сам звонок и «Создана заявка»). Строка самого звонка не меняется: его обращение идёт отдельной записью рядом с ним.
 * Порядок сервера (новые первыми) сохраняется.
 *
 *  title            «Повторное обращение»;
 *  otherSourceLabel «ДРУГОЙ ИСТОЧНИК» — appeal.other_source; единственное, что выделяется цветом;
 *  sameSourceLabel  «тот же источник» — у обращения с тем же источником; мелкий серый текст без рамки;
 *  line             вторая строка (см. appealLine);
 *  callId           звонок обращения: запись ставится сразу после него;
 *  double           у лида — ссылка на созданную им заявку-дубль (статус дубля не показываем).
 *
 * @returns {Array<{id:number,channel:string,occurredAt:string,callId:number|null,title:string,otherSourceLabel:string|null,
 *   sameSourceLabel:string|null,line:string|null,double:null|{id:number,title:string}}>}
 */
function appealBlocks(appeals) {
    return (Array.isArray(appeals) ? appeals : [])
        .filter(function (appeal) {
            return appeal && (appeal.channel === 'call' || appeal.channel === 'lead') && appeal.kind === 'repeat';
        })
        .map(function (appeal) {
            var lead = appeal.lead_request;
            return {
                id: appeal.id,
                channel: appeal.channel,
                occurredAt: appeal.occurred_at,
                callId: appeal.call ? appeal.call.id : null,
                title: TEXTS.feedRepeat,
                otherSourceLabel: appeal.other_source ? TEXTS.feedOtherSource : null,
                sameSourceLabel: appeal.other_source === false ? TEXTS.feedSameSource : null,
                line: appealLine(appeal),
                double: lead ? { id: lead.id, title: TEXTS.feedDouble + lead.id } : null,
            };
        });
}

module.exports = {
    TEXTS: TEXTS,
    REQUEST_PEOPLE: REQUEST_PEOPLE,
    VARIANT: VARIANT,
    CRM_UTC_OFFSET_MINUTES: CRM_UTC_OFFSET_MINUTES,
    MAX_CALL_AGE_MS: MAX_CALL_AGE_MS,
    MAX_QUEUE_WAIT_MS: MAX_QUEUE_WAIT_MS,
    RETRY_DELAYS_MS: RETRY_DELAYS_MS,
    plural: plural,
    parseCrmTime: parseCrmTime,
    referenceTime: referenceTime,
    isOwnCall: isOwnCall,
    formatDate: formatDate,
    variantOf: variantOf,
    incomingView: incomingView,
    outgoingView: outgoingView,
    appealBlocks: appealBlocks,
};
